/** Sección de artículos del sitio público (discreta: solo enlace en el pie de página).
 *
 *  Los artículos se EDITAN SIN CÓDIGO desde CP Admin (/admin → «Artículos»): viven en Firestore,
 *  documento `siteContent/articulos`, como cualquier otra sección del sitio. Este archivo solo
 *  convierte ese contenido (texto simple) en bloques para la plantilla; no guarda artículos.
 *
 *  - Interruptor general: `publicos` (casillero «Mostrar la sección de artículos»). Apagado, la
 *    sección no existe para el visitante: /articulos y /articulos/<slug> responden «no encontrado»
 *    con `noindex`, el pie no muestra el enlace y el build no los prerenderiza ni los pone en el sitemap.
 *  - Cada artículo tiene su casillero «Publicado» (borrador mientras esté apagado).
 *  - Los cambios se ven en vivo; para que Google reciba el HTML ya armado (prerender y sitemap) hay que
 *    volver a publicar el sitio (el build lee el contenido vivo).
 *
 *  Formato del cuerpo (texto simple, ver `parsearCuerpo`):
 *    ## Título de sección     → subtítulo
 *    texto suelto             → párrafo (un párrafo por bloque separado por una línea en blanco)
 *    - elemento               → lista (una línea por elemento)
 *    !! Título del recuadro   → recuadro destacado (p. ej. seguridad); sus líneas «- …» son los puntos
 *  Los borradores y el orden sugerido están en `docs/contenido/articulos/`. */

/** Firma de un artículo. Por defecto firma el dueño (decisión 2026-11-01); otro profesor firma solo si
 *  él escribe el artículo: en ese caso se completan los campos de firma de ese artículo. No usar cifras de
 *  alumnos ni de años de experiencia en la firma (decisión del dueño). */
export type FirmaArticulo = { nombre: string; cargo: string; credencial?: string };

export const FIRMA_POR_DEFECTO: FirmaArticulo = {
  nombre: "José Vargas Cantillano",
  cargo: "Fundador de Calistenia Progresiva",
  credencial: "Profesor de Educación Física y Salud — Universidad Católica Silva Henríquez",
};

export type BloqueArticulo =
  | { tipo: "h2"; texto: string }
  | { tipo: "p"; texto: string }
  | { tipo: "ul"; items: string[] }
  | { tipo: "aviso"; titulo: string; items: string[] };

export type Articulo = {
  slug: string;
  titulo: string;
  descripcion: string;
  publicado: string;
  revisado: string;
  firma?: FirmaArticulo;
  etiqueta?: string;
  cuerpo: BloqueArticulo[];
  enlaces?: [string, string][];
};

/** Un artículo tal como se edita en CP Admin (todo texto). */
export type ArticuloEditable = {
  slug?: string;
  titulo?: string;
  descripcion?: string;
  etiqueta?: string;
  publicado?: string;
  revisado?: string;
  /** Casillero «Publicado»: sin marcar es borrador. */
  visible?: boolean;
  firmaNombre?: string;
  firmaCargo?: string;
  firmaCredencial?: string;
  cuerpo?: string;
  /** Una línea por enlace: «Texto del botón | /ruta». */
  enlaces?: string;
};

export type ContenidoArticulos = { publicos?: boolean; items?: ArticuloEditable[] };

export const SLUG_VALIDO = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Convierte el texto simple del cuerpo en bloques (ver el formato arriba). */
export function parsearCuerpo(texto: string): BloqueArticulo[] {
  const bloques: BloqueArticulo[] = [];
  let parrafo: string[] = [];
  let lista: string[] | null = null;
  let aviso: { tipo: "aviso"; titulo: string; items: string[] } | null = null;
  const cerrarParrafo = () => { if (parrafo.length) { bloques.push({ tipo: "p", texto: parrafo.join(" ") }); parrafo = []; } };
  const cerrarLista = () => { if (lista && lista.length) bloques.push({ tipo: "ul", items: lista }); lista = null; };
  const cerrarAviso = () => { if (aviso) { bloques.push(aviso); aviso = null; } };
  const cerrarTodo = () => { cerrarParrafo(); cerrarLista(); cerrarAviso(); };
  for (const cruda of String(texto ?? "").replace(/\r\n?/g, "\n").split("\n")) {
    const l = cruda.trim();
    if (l === "") { cerrarTodo(); continue; }
    if (l.startsWith("## ")) { cerrarTodo(); bloques.push({ tipo: "h2", texto: l.slice(3).trim() }); continue; }
    if (l.startsWith("!! ")) { cerrarTodo(); aviso = { tipo: "aviso", titulo: l.slice(3).trim(), items: [] }; continue; }
    if (/^[-•]\s+/.test(l)) {
      const item = l.replace(/^[-•]\s+/, "").trim();
      if (aviso) { (aviso as { items: string[] }).items.push(item); continue; }
      cerrarParrafo();
      if (!lista) lista = [];
      lista.push(item);
      continue;
    }
    if (aviso) { cerrarAviso(); }
    cerrarLista();
    parrafo.push(l);
  }
  cerrarTodo();
  return bloques;
}

/** Inverso de `parsearCuerpo` (para cargar artículos ya escritos como bloques). */
export function cuerpoATexto(bloques: BloqueArticulo[]): string {
  return bloques.map((b) => {
    if (b.tipo === "h2") return `## ${b.texto}`;
    if (b.tipo === "p") return b.texto;
    if (b.tipo === "ul") return b.items.map((x) => `- ${x}`).join("\n");
    return [`!! ${b.titulo}`, ...b.items.map((x) => `- ${x}`)].join("\n");
  }).join("\n\n");
}

/** «Texto | /ruta» por línea → [texto, ruta]. Solo rutas internas (empiezan con «/»). */
export function parsearEnlaces(texto: string): [string, string][] {
  const out: [string, string][] = [];
  for (const l of String(texto ?? "").split("\n")) {
    const [t, r] = l.split("|").map((x) => x.trim());
    if (t && r && r.startsWith("/") && !r.startsWith("//")) out.push([t, r]);
  }
  return out;
}

/** Artículo editable → artículo para la plantilla; `null` si le falta lo mínimo (slug válido, título, cuerpo). */
export function articuloDesdeContenido(e: ArticuloEditable): Articulo | null {
  const slug = (e.slug ?? "").trim();
  const titulo = (e.titulo ?? "").trim();
  const cuerpo = parsearCuerpo(e.cuerpo ?? "");
  if (!SLUG_VALIDO.test(slug) || !titulo || cuerpo.length === 0) return null;
  const nombre = (e.firmaNombre ?? "").trim();
  const credencial = (e.firmaCredencial ?? "").trim();
  const publicado = (e.publicado ?? "").trim();
  const enlaces = parsearEnlaces(e.enlaces ?? "");
  return {
    slug,
    titulo,
    descripcion: (e.descripcion ?? "").trim(),
    publicado,
    revisado: (e.revisado ?? "").trim() || publicado,
    etiqueta: (e.etiqueta ?? "").trim() || undefined,
    firma: nombre ? { nombre, cargo: (e.firmaCargo ?? "").trim(), ...(credencial ? { credencial } : {}) } : undefined,
    cuerpo,
    ...(enlaces.length ? { enlaces } : {}),
  };
}

/** Lo que el sitio muestra: nada con el interruptor apagado; si no, los artículos marcados «Publicado»
 *  y completos, del más reciente al más antiguo (sin repetir slug). */
export function articulosPublicados(c: ContenidoArticulos | null | undefined): Articulo[] {
  if (!c?.publicos) return [];
  const vistos = new Set<string>();
  const out: Articulo[] = [];
  for (const e of c.items ?? []) {
    if (!e?.visible) continue;
    const a = articuloDesdeContenido(e);
    if (!a || vistos.has(a.slug)) continue;
    vistos.add(a.slug);
    out.push(a);
  }
  return out.sort((x, y) => (y.publicado || "").localeCompare(x.publicado || ""));
}

/** ¿Hay sección de artículos que mostrar (pie de página, rutas, sitemap)? */
export function hayArticulos(c: ContenidoArticulos | null | undefined): boolean {
  return articulosPublicados(c).length > 0;
}

export function articuloPorSlug(c: ContenidoArticulos | null | undefined, slug: string): Articulo | undefined {
  return articulosPublicados(c).find((a) => a.slug === slug);
}

/** «2026-12-05» → «5 de diciembre de 2026». */
export function fechaLarga(iso: string): string {
  const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} de ${MESES[Number(m[2]) - 1]} de ${m[1]}`;
}
